import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useHelpdeskMutations, useTicketCategories } from '../api'

/** Categories: what each one promises, who it goes to, and whether it is confidential. */
export function HelpdeskSetupPage() {
  useBreadcrumb()
  const categories = useTicketCategories()
  const roles = useLookup<{ id: string; code: string; name: string }>('roles')
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveCategory, retireCategory } = useHelpdeskMutations()

  const [draft, setDraft] = useState<{
    id?: string; code: string; name: string; description: string
    responseMinutes: string; resolutionMinutes: string; escalateAfterMinutes: string
    escalateToRoleId: string; defaultAssigneeRoleId: string
    confidential: boolean; requiresApproval: boolean
  } | null>(null)
  const [retiring, setRetiring] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Helpdesk setup"
        subtitle="What each kind of request promises, and who hears about it when that promise is missed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', name: '', description: '', responseMinutes: '',
                resolutionMinutes: '', escalateAfterMinutes: '', escalateToRoleId: '',
                defaultAssigneeRoleId: '', confidential: false, requiresApproval: false,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New category
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        No service level ships. "Twenty-four hours" is a promise you make, not a fact about
        helpdesks, and it is the number quoted back in a complaint. A category with no targets
        gives its tickets no due time at all — not an instant one and not an unlimited one.
      </p>

      <Card>
        {categories.isPending && <Skeleton className="m-4 h-24" />}
        {categories.isError && (
          <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
        )}
        {categories.isSuccess && categories.data.length === 0 && (
          <EmptyState
            title="No category is defined"
            description="Nothing can be raised until one exists. A category carries the service level, who picks it up, whether it is confidential, and whether the work needs approving first."
          />
        )}
        {categories.isSuccess && categories.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Category</th>
                  <th className="p-3 text-start font-medium">What it promises</th>
                  <th className="p-3 text-start font-medium">Escalates to</th>
                  <th className="p-3 text-end font-medium">Open</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {categories.data.map((category) => (
                  <tr key={category.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">
                      {category.code}
                      {category.confidential && (
                        <div className="text-xs text-slate-500">confidential</div>
                      )}
                      {category.requiresApproval && (
                        <div className="text-xs text-slate-500">needs approval</div>
                      )}
                    </td>
                    <td className="p-3">{category.name}</td>
                    <td className="p-3 max-w-md text-xs text-slate-600">{category.slaNote}</td>
                    <td className="p-3 text-xs text-slate-600">
                      {category.escalateToRoleCode ?? 'Nobody'}
                    </td>
                    <td className="p-3 text-end">{category.openTickets}</td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setError(null)
                          setDraft({
                            id: category.id,
                            code: category.code,
                            name: category.name,
                            description: category.description ?? '',
                            responseMinutes: category.responseMinutes?.toString() ?? '',
                            resolutionMinutes: category.resolutionMinutes?.toString() ?? '',
                            escalateAfterMinutes:
                              category.escalateAfterMinutes?.toString() ?? '',
                            escalateToRoleId: category.escalateToRoleId ?? '',
                            defaultAssigneeRoleId: category.defaultAssigneeRoleId ?? '',
                            confidential: category.confidential,
                            requiresApproval: category.requiresApproval,
                          })
                        }}
                      >
                        Edit
                      </Button>
                      {category.active && (
                        <Button
                          type="button" variant="outline"
                          onClick={() => {
                            setError(null)
                            setRetiring({ id: category.id, name: category.name })
                          }}
                        >
                          Retire
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this category'}?`}
        description="It stops being offered on new tickets. Every closed ticket keeps naming it, and last quarter's service-level report is grouped by exactly that — so this is never a delete."
        confirmLabel="Retire it"
        loading={retireCategory.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retireCategory.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That category could not be retired.'),
          })
        }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title={draft?.id ? 'Edit category' : 'New category'}
        description="Leave the times blank for a category that promises nothing."
        submitLabel="Save category"
        loading={saveCategory.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveCategory.mutateAsync({
                id: draft.id,
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  code: draft.code,
                  name: draft.name,
                  description: draft.description || null,
                  defaultAssigneeRoleId: draft.defaultAssigneeRoleId || null,
                  responseMinutes: draft.responseMinutes ? Number(draft.responseMinutes) : null,
                  resolutionMinutes: draft.resolutionMinutes
                    ? Number(draft.resolutionMinutes) : null,
                  escalateAfterMinutes: draft.escalateAfterMinutes
                    ? Number(draft.escalateAfterMinutes) : null,
                  escalateToRoleId: draft.escalateToRoleId || null,
                  confidential: draft.confidential,
                  requiresApproval: draft.requiresApproval,
                  active: true,
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That category could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="category-code">Code</Label>
              <Input
                id="category-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="category-name">Name</Label>
              <Input
                id="category-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="category-response">First reply within, minutes</Label>
              <Input
                id="category-response" type="number" value={draft.responseMinutes}
                onChange={(event) =>
                  setDraft({ ...draft, responseMinutes: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Blank means no reply time is promised or measured.
              </p>
            </div>
            <div>
              <Label htmlFor="category-resolution">Resolved within, minutes</Label>
              <Input
                id="category-resolution" type="number" value={draft.resolutionMinutes}
                onChange={(event) =>
                  setDraft({ ...draft, resolutionMinutes: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                No default: this is a promise you are making.
              </p>
            </div>
            <div>
              <Label htmlFor="category-escalate">Escalate after, minutes</Label>
              <Input
                id="category-escalate" type="number" value={draft.escalateAfterMinutes}
                onChange={(event) =>
                  setDraft({ ...draft, escalateAfterMinutes: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Runs from the resolution time, so that has to be set too.
              </p>
            </div>
            <div>
              <Label htmlFor="category-escalate-role">Escalate to</Label>
              <Select
                id="category-escalate-role"
                className="w-full"
                value={draft.escalateToRoleId}
                onChange={(event) =>
                  setDraft({ ...draft, escalateToRoleId: event.target.value })}
              >
                <option value="">Nobody</option>
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name ?? role.code}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Escalating to nobody is not escalation, it is a delay with a name.
              </p>
            </div>
            <div>
              <Label htmlFor="category-assignee">Picked up by</Label>
              <Select
                id="category-assignee"
                className="w-full"
                value={draft.defaultAssigneeRoleId}
                onChange={(event) =>
                  setDraft({ ...draft, defaultAssigneeRoleId: event.target.value })}
              >
                <option value="">Nobody in particular</option>
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name ?? role.code}</option>
                ))}
              </Select>
            </div>
            <fieldset className="space-y-2 sm:col-span-2">
              <legend className="text-sm font-medium">How it behaves</legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  id="category-confidential" type="checkbox" className="size-4"
                  checked={draft.confidential}
                  onChange={(event) =>
                    setDraft({ ...draft, confidential: event.target.checked })}
                />
                Confidential — grievances and the like
              </label>
              <p className="text-xs text-slate-600">
                A confidential ticket is readable by the person who raised it, whoever it is
                assigned to, and holders of helpdesk:confidential. Nobody else sees it in any
                list or report.
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  id="category-approval" type="checkbox" className="size-4"
                  checked={draft.requiresApproval}
                  onChange={(event) =>
                    setDraft({ ...draft, requiresApproval: event.target.checked })}
                />
                The work needs approving before it starts
              </label>
            </fieldset>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
